import React from 'react';
import { User } from '../../types';
import Sidebar from '../Sidebar';
import ModalRenderer from '../ModalRenderer';
import { useModal } from '../../contexts/ModalContext';

export interface AppLayoutProps {
  user: User;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  handleNavigate: (path: string) => void;
  handleLogout: () => void;
  handleProfileClick: () => void;
  handleAssistantButtonClick: () => void;
  refreshFounders: (() => Promise<void>) | null;
  activeTab: { id: string; name: string } | null;
  location: { pathname: string };
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  user,
  sidebarCollapsed,
  setSidebarCollapsed,
  handleNavigate,
  handleLogout,
  handleProfileClick,
  handleAssistantButtonClick,
  refreshFounders,
  activeTab,
  location,
  children,
}) => {
  const { openModal, closeModal, isModalOpen } = useModal();

  return (
    <div
      className={`app ${isModalOpen('chatBox') ? 'chat-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
    >
      <Sidebar
        currentPath={location.pathname}
        onNavigate={handleNavigate}
        user={user}
        onLogout={handleLogout}
        onProfileClick={handleProfileClick}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => {
          const newState = !sidebarCollapsed;
          setSidebarCollapsed(newState);
          localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
        }}
        onChatClick={() => {
          if (isModalOpen('chatBox')) {
            closeModal();
          } else {
            openModal('chatBox', {
              userId: user.id,
              context: {
                currentRoute: location.pathname,
                selectedFounders: [],
                activeTab: activeTab,
              },
              onRefreshFounders: refreshFounders || undefined,
            });
          }
        }}
        onWalkthroughClick={() => openModal('productWalkthrough')}
        onChromeExtensionWalkthroughClick={() => openModal('feedback')}
        onReportBug={() => openModal('feedback')}
      />
      <div className="main-wrapper">
        <main className="main-content">{children}</main>

        {/* Centralized modal rendering */}
        <ModalRenderer location={location} />

        {/* Floating Assistant Button */}
        <button
          className="floating-assistant-button"
          onClick={() => {
            handleAssistantButtonClick(); // Trigger confetti
            // Toggle chat modal
            if (isModalOpen('chatBox')) {
              closeModal();
            } else {
              openModal('chatBox', {
                userId: user.id,
                context: {
                  currentRoute: location.pathname,
                  selectedFounders: [],
                  activeTab: activeTab,
                },
                onRefreshFounders: refreshFounders || undefined,
              });
            }
          }}
          title="Assistant"
        >
          🐭
        </button>
      </div>
    </div>
  );
};

export default AppLayout;
